class CartDrawerFreeShipping extends HTMLElement {
  constructor() {
    super();
    this.config = {};
    this.previousFocus = null;
    this.pendingItemsRetry = false;
    this.scrollYBeforeOpen = 0;
    this.boundKeydown = this.onKeydown.bind(this);
    this.boundDocumentClick = this.onDocumentClick.bind(this);
    this.boundDocumentSubmit = this.onDocumentSubmit.bind(this);
    this.boundDocumentCartAddComplete = this.onDocumentCartAddComplete.bind(this);
  }

  connectedCallback() {
    this.config = this.readConfig();
    this.cacheElements();
    this.bindEvents();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundDocumentClick);
    document.removeEventListener('submit', this.boundDocumentSubmit, true);
    document.removeEventListener('cart:updated', this.boundDocumentCartAddComplete);
    document.removeEventListener('cart:refresh', this.boundDocumentCartAddComplete);
    document.removeEventListener('ajaxProduct:added', this.boundDocumentCartAddComplete);
    document.removeEventListener('product:added', this.boundDocumentCartAddComplete);
    document.removeEventListener('keydown', this.boundKeydown);
  }

  readConfig() {
    const script = document.querySelector('[data-cart-drawer-config]');
    if (!script) return {};

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return {};
    }
  }

  cacheElements() {
    this.panel = this.querySelector('.cart-drawer__panel');
    this.items = this.querySelector('[data-cart-drawer-items]');
    this.status = this.querySelector('[data-cart-drawer-status]');
    this.title = this.querySelector('[data-cart-drawer-title]');
    this.subtotal = this.querySelector('[data-cart-drawer-subtotal]');
    this.discountRow = this.querySelector('[data-cart-drawer-discount-row]');
    this.discountLabel = this.querySelector('[data-cart-drawer-discount-label]');
    this.discount = this.querySelector('[data-cart-drawer-discount]');
    this.total = this.querySelector('[data-cart-drawer-total]');
    this.checkout = this.querySelector('[data-cart-drawer-checkout]');
    this.footer = this.querySelector('[data-cart-drawer-footer]');
    this.tiers = this.querySelectorAll('[data-cart-drawer-tier]');
    this.upsellList = this.querySelector('[data-cart-drawer-upsell-list]');
    this.closeButtons = this.querySelectorAll('[data-cart-drawer-close]');
  }

  bindEvents() {
    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));
    this.addEventListener('click', (event) => this.onClick(event));
    this.addEventListener('change', (event) => this.onChange(event));
    document.addEventListener('click', this.boundDocumentClick);
    document.addEventListener('submit', this.boundDocumentSubmit, true);
    document.addEventListener('cart:updated', this.boundDocumentCartAddComplete);
    document.addEventListener('cart:refresh', this.boundDocumentCartAddComplete);
    document.addEventListener('ajaxProduct:added', this.boundDocumentCartAddComplete);
    document.addEventListener('product:added', this.boundDocumentCartAddComplete);
  }

  onDocumentClick(event) {
    const trigger = this.findOpenTrigger(event.target);
    if (!trigger) {
      const addButton = this.findAddToCartButton(event.target);
      if (addButton) {
        const form = addButton.closest('form');
        if (form) {
          event.preventDefault();
          this.addFormToCart(form, addButton);
        } else {
          this.openAfterThemeCartAdd(addButton);
        }
      }
      return;
    }

    event.preventDefault();
    this.open(trigger);
  }

  findOpenTrigger(target) {
    if (!(target instanceof Element)) return null;

    const openSelector = this.config.selectors?.open || this.dataset.openSelector;
    if (openSelector) {
      const configuredTrigger = target.closest(openSelector);
      if (configuredTrigger) return configuredTrigger;
    }

    const cartLink = target.closest('a[href]');
    if (!cartLink) return null;

    try {
      const cartUrl = new URL(this.config.routes?.cart || '/cart', window.location.origin);
      const linkUrl = new URL(cartLink.getAttribute('href'), window.location.origin);
      return linkUrl.pathname === cartUrl.pathname ? cartLink : null;
    } catch (error) {
      return null;
    }
  }

  findAddToCartButton(target) {
    if (!(target instanceof Element)) return null;

    const buttonSelector = this.config.selectors?.addToCartButton;
    if (buttonSelector) {
      const configuredButton = target.closest(buttonSelector);
      if (configuredButton) return configuredButton;
    }

    const button = target.closest('button, input[type="submit"], [type="submit"]');
    if (!button) return null;

    const form = button.closest('form');
    return this.isCartAddForm(form) ? button : null;
  }

  onDocumentSubmit(event) {
    const form = event.target;
    if (!this.isCartAddForm(form)) return;
    if (form.closest('[data-cart-drawer]')) return;

    event.preventDefault();
    this.addFormToCart(form, event.submitter);
  }

  onDocumentCartAddComplete(event) {
    if (event.target?.closest?.('[data-cart-drawer]')) return;
    this.openAfterThemeCartAdd(document.activeElement);
  }

  isCartAddForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;

    const formSelector = this.config.selectors?.productForm;
    if (formSelector && form.matches(formSelector)) return true;

    const action = form.getAttribute('action') || '';
    return action.includes('/cart/add') || Boolean(form.querySelector('[name="id"]'));
  }

  open(trigger = document.activeElement) {
    this.previousFocus = trigger;
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    this.lockPageScroll();
    document.documentElement.classList.add('cart-drawer-is-open');
    document.addEventListener('keydown', this.boundKeydown);
    this.panel?.focus();
    this.refreshCart();
  }

  openAfterThemeCartAdd(trigger = document.activeElement) {
    window.setTimeout(() => {
      this.open(trigger);
    }, 350);
  }

  async refreshCart() {
    try {
      const cart = await this.fetchCart();
      this.renderCart(cart);
    } catch (error) {
      this.announce('');
    }
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('hidden', '');
    document.documentElement.classList.remove('cart-drawer-is-open');
    this.unlockPageScroll();
    document.removeEventListener('keydown', this.boundKeydown);

    const fallbackSelector = this.config.selectors?.focusAfterClose;
    const fallbackFocus = fallbackSelector ? document.querySelector(fallbackSelector) : null;
    (this.previousFocus || fallbackFocus)?.focus?.();
  }

  lockPageScroll() {
    if (document.body.dataset.cartDrawerScrollLocked === 'true') return;

    this.scrollYBeforeOpen = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.dataset.cartDrawerScrollLocked = 'true';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.scrollYBeforeOpen}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  }

  unlockPageScroll() {
    if (document.body.dataset.cartDrawerScrollLocked !== 'true') return;

    const scrollY = Math.abs(parseInt(document.body.style.top || '0', 10)) || this.scrollYBeforeOpen || 0;
    delete document.body.dataset.cartDrawerScrollLocked;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
    if (event.key === 'Tab') this.trapFocus(event);
  }

  trapFocus(event) {
    const focusable = this.panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  onClick(event) {
    const upsellButton = event.target.closest('[data-cart-drawer-upsell-add]');
    if (upsellButton) {
      this.addUpsell(upsellButton);
      return;
    }

    const upsellPrev = event.target.closest('[data-cart-drawer-upsell-prev]');
    if (upsellPrev) {
      this.scrollUpsells(-1);
      return;
    }

    const upsellNext = event.target.closest('[data-cart-drawer-upsell-next]');
    if (upsellNext) {
      this.scrollUpsells(1);
      return;
    }

    const line = event.target.closest('[data-cart-drawer-item]');
    if (!line) return;

    const input = line.querySelector('[data-quantity-input]');
    const currentQuantity = Number(input?.value || 0);

    if (event.target.closest('[data-quantity-minus]')) {
      this.updateLine(line, Math.max(currentQuantity - 1, 0));
    }

    if (event.target.closest('[data-quantity-plus]')) {
      this.updateLine(line, currentQuantity + 1);
    }

    if (event.target.closest('[data-cart-drawer-remove]')) {
      this.updateLine(line, 0);
    }
  }

  onChange(event) {
    if (!event.target.matches('[data-quantity-input]')) return;
    const line = event.target.closest('[data-cart-drawer-item]');
    this.updateLine(line, Math.max(Number(event.target.value || 0), 0));
  }

  async addUpsell(button) {
    const id = Number(button.dataset.variantId);
    if (!id) return;

    this.setLoading(true);
    try {
      await fetch(this.config.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id, quantity: 1 })
      });
      const cart = await this.fetchCart();
      this.renderCart(cart);
      this.announce(this.config.i18n?.updated || '');
      this.dispatchEvent(new CustomEvent('cart-drawer:updated', { bubbles: true, detail: { cart } }));
    } catch (error) {
      this.announce('');
    } finally {
      this.setLoading(false);
    }
  }

  scrollUpsells(direction) {
    if (!this.upsellList) return;

    const card = this.upsellList.querySelector('.cart-drawer__upsell-card');
    const gap = Number.parseFloat(getComputedStyle(this.upsellList).columnGap || getComputedStyle(this.upsellList).gap || 0);
    const distance = card ? card.getBoundingClientRect().width + gap : this.upsellList.clientWidth;

    this.upsellList.scrollBy({
      left: distance * direction,
      behavior: 'smooth'
    });
  }

  async addFormToCart(form, submitter) {
    this.setLoading(true);
    if (submitter) submitter.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData(form);
      const response = await fetch(this.config.routes?.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });

      if (!response.ok) throw new Error('Cart add failed');

      const cart = await this.fetchCart();
      this.renderCart(cart);
      this.open(submitter || form);
      this.dispatchEvent(new CustomEvent('cart-drawer:updated', { bubbles: true, detail: { cart } }));
    } catch (error) {
      this.announce('');
    } finally {
      if (submitter) submitter.removeAttribute('aria-busy');
      this.setLoading(false);
    }
  }

  async updateLine(line, quantity) {
    if (!line) return;

    this.setLoading(true);
    this.announce(this.config.i18n?.loading || '');

    try {
      const response = await fetch(this.config.routes?.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.dataset.lineKey, quantity })
      });

      if (!response.ok) throw new Error();

      const cart = await response.json();
      this.renderCart(cart);
      this.announce(this.config.i18n?.updated || '');
      this.dispatchEvent(new CustomEvent('cart-drawer:updated', { bubbles: true, detail: { cart } }));
    } catch (error) {
      this.announce('');
    } finally {
      this.setLoading(false);
    }
  }

  async fetchCart() {
    const response = await fetch(`${this.config.routes?.cart || '/cart'}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }

  renderCart(cart) {
    if (!this.items) return;

    const items = Array.isArray(cart.items) ? cart.items : (Array.isArray(cart.line_items) ? cart.line_items : []);
    const itemCountFromItems = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const cartItemCount = Number(cart.item_count || 0);
    const itemCount = cartItemCount > 0 ? cartItemCount : itemCountFromItems;

    if (items.length) {
      this.pendingItemsRetry = false;
      this.items.innerHTML = items.map((item, index) => this.renderItem(item, index)).join('');
    } else if (itemCount > 0) {
      this.items.innerHTML = this.renderEmptyState(this.config.i18n?.loading || '');
      if (!this.pendingItemsRetry) {
        this.pendingItemsRetry = true;
        window.setTimeout(() => this.refreshCart(), 300);
      }
    } else {
      this.pendingItemsRetry = false;
      this.items.innerHTML = this.renderEmptyState(this.config.i18n?.empty || '');
    }

    if (this.footer) this.footer.hidden = itemCount <= 0;

    const subtotalFromItems = items.reduce((sum, item) => {
      const quantity = Number(item.quantity || 0);
      const linePrice = Number(item.final_line_price ?? item.line_price ?? item.original_line_price ?? 0);
      const unitPrice = Number(item.final_price ?? item.price ?? 0);
      return sum + (linePrice > 0 ? linePrice : unitPrice * quantity);
    }, 0);
    const cartSubtotal = Number(cart.items_subtotal_price || cart.original_total_price || cart.total_price || 0);
    const subtotal = cartSubtotal > 0 ? cartSubtotal : subtotalFromItems;
    const cartTotal = Number(cart.total_price || 0);
    const safeCartTotal = cartTotal > 0 || subtotal === 0 ? cartTotal : subtotal;
    const shopifyDiscount = Number(cart.total_discount || 0) || Math.max(subtotal - safeCartTotal, 0);
    const achievedTier = this.getAchievedTier(subtotal);
    const estimatedDiscount = this.calculateTierDiscount(subtotal, achievedTier);
    const displayDiscount = shopifyDiscount > 0 ? shopifyDiscount : estimatedDiscount;
    const displayTotal = Math.max(subtotal - displayDiscount, 0);

    if (this.title) this.title.textContent = (this.config.i18n?.cartTitle || 'Cart ([count])').replace('[count]', itemCount);
    if (this.subtotal) this.subtotal.textContent = this.formatMoney(subtotal);
    if (this.discountLabel && achievedTier?.percent) {
      const labelTemplate = this.discountRow?.dataset.discountLabelTemplate || this.discountLabel.textContent || '';
      this.discountLabel.textContent = labelTemplate
        .replace('[percent]', achievedTier.percent || '')
        .replace('[discount_amount]', this.formatMoney(this.calculateTierDiscount(subtotal, achievedTier)));
    }
    if (this.discount) this.discount.textContent = displayDiscount > 0 ? `-${this.formatMoney(displayDiscount)}` : this.formatMoney(0);
    if (this.discountRow && this.discountRow.dataset.hideZeroDiscount === 'true') {
      this.discountRow.hidden = displayDiscount <= 0;
    }
    if (this.total) this.total.textContent = this.formatMoney(shopifyDiscount > 0 ? safeCartTotal : displayTotal);

    this.updateCartCount(itemCount);
    this.updateTiers(safeCartTotal);
    this.updateCheckoutDiscount(subtotal);
  }

  renderItem(item, index) {
    const fields = this.config.itemFields || {};
    const title = this.resolveField(item, fields.title, item.product_title || item.title);
    const variant = this.resolveField(item, fields.variant, item.variant_title);
    const image = this.resolveField(item, fields.image, item.image);
    const price = this.resolveField(item, fields.price, item.final_line_price);
    const compareAtPrice = this.resolveField(item, fields.compareAtPrice, item.original_line_price);
    const imageMarkup = image ? `<img src="${this.escapeAttribute(image)}" alt="${this.escapeAttribute(title)}" loading="lazy">` : '';
    const compareMarkup = compareAtPrice > price ? `<s>${this.formatMoney(compareAtPrice)}</s>` : '';

    return `
      <article class="cart-drawer__item" data-cart-drawer-item data-line-key="${this.escapeAttribute(item.key)}" data-line-index="${index + 1}">
        <a class="cart-drawer__item-media" href="${this.escapeAttribute(item.url || '#')}">${imageMarkup}</a>
        <div class="cart-drawer__item-main">
          <a class="cart-drawer__item-title" href="${this.escapeAttribute(item.url || '#')}">${this.escapeHtml(title)}</a>
          ${variant && variant !== 'Default Title' ? `<p class="cart-drawer__item-variant">${this.escapeHtml(variant)}</p>` : ''}
          <div class="cart-drawer__item-actions">
            <label class="visually-hidden">${this.escapeHtml(this.config.i18n?.quantityLabel || '')}</label>
            <div class="cart-drawer__quantity" data-cart-drawer-quantity>
              <button type="button" data-quantity-minus aria-label="${this.escapeAttribute(this.config.i18n?.quantityDecrease || '')}">&minus;</button>
              <input type="number" min="0" value="${Number(item.quantity || 0)}" data-quantity-input>
              <button type="button" data-quantity-plus aria-label="${this.escapeAttribute(this.config.i18n?.quantityIncrease || '')}">+</button>
            </div>
            <button class="cart-drawer__remove" type="button" data-cart-drawer-remove>${this.escapeHtml(this.config.i18n?.remove || '')}</button>
          </div>
        </div>
        <div class="cart-drawer__price">
          ${compareMarkup}
          <span>${this.formatMoney(price)}</span>
        </div>
      </article>
    `;
  }

  renderEmptyState(message) {
    const actions = (this.config.emptyActions || [])
      .filter((action) => action?.label && action?.url)
      .map((action) => `<a href="${this.escapeAttribute(action.url)}">${this.escapeHtml(action.label)}</a>`)
      .join('');

    return `
      <div class="cart-drawer__empty" data-cart-drawer-empty>
        <p>${this.escapeHtml(message)}</p>
        ${actions ? `<div class="cart-drawer__empty-actions">${actions}</div>` : ''}
      </div>
    `;
  }

  updateTiers(totalPrice) {
    this.tiers.forEach((tier, index) => {
      const tierConfigIndex = Number(tier.dataset.tierIndex || index);
      const configTier = this.config.tiers?.[tierConfigIndex] || {};
      const threshold = Number(configTier.thresholdCents || tier.dataset.thresholdCents || 0);
      const progress = threshold > 0 ? Math.min((totalPrice / threshold) * 100, 100) : 100;
      const text = tier.querySelector('.cart-drawer__tier-text');
      const template = configTier.textTemplate || tier.dataset.tierTextTemplate || this.config.i18n?.tierLabel || '';
      const fixedDiscount = Number(configTier.fixedDiscountCents || tier.dataset.tierFixedDiscountCents || 0);

      tier.querySelector('.cart-drawer__tier-bar')?.style.setProperty('--cart-drawer-progress', `${progress}%`);
      if (text) {
        text.innerHTML = template
          .replace('[amount]', this.formatMoney(threshold))
          .replace('[percent]', configTier.percent || tier.dataset.tierPercent || '')
          .replace('[discount_amount]', this.formatMoney(fixedDiscount))
          .replace('[suffix]', configTier.suffix || tier.dataset.tierSuffix || '');
      }
    });
  }

  updateCheckoutDiscount(totalPrice) {
    if (!this.checkout) return;

    const achievedTier = this.getAchievedTier(totalPrice, true);

    if (!achievedTier) {
      this.checkout.setAttribute('href', '/checkout');
      return;
    }

    const code = encodeURIComponent(achievedTier.discountCode);
    this.checkout.setAttribute('href', `/discount/${code}?redirect=/checkout`);
  }

  getAchievedTier(totalPrice, requireDiscountCode = false) {
    return (this.config.tiers || [])
      .filter((tier) => tier.enabled !== false)
      .filter((tier) => Number(tier.thresholdCents || 0) <= totalPrice)
      .filter((tier) => !requireDiscountCode || tier.discountCode)
      .sort((a, b) => Number(b.thresholdCents || 0) - Number(a.thresholdCents || 0))[0];
  }

  calculateTierDiscount(subtotal, tier) {
    if (!tier) return 0;

    if (tier.discountType === 'fixed_amount') {
      return Math.min(Number(tier.fixedDiscountCents || 0), subtotal);
    }

    return tier.percent ? Math.round(subtotal * Number(tier.percent) / 100) : 0;
  }

  resolveField(source, path, fallback) {
    if (!path) return fallback;
    return path.split('.').reduce((value, key) => (value == null ? value : value[key]), source) ?? fallback;
  }

  updateCartCount(count) {
    const selector = this.config.selectors?.cartCount || this.dataset.cartCountSelector;
    if (!selector) return;
    document.querySelectorAll(selector).forEach((node) => {
      node.textContent = count;
    });
  }

  setLoading(isLoading) {
    this.toggleAttribute('aria-busy', isLoading);
    this.querySelectorAll('button, input').forEach((control) => {
      control.disabled = isLoading;
    });
  }

  announce(message) {
    if (this.status) this.status.textContent = message;
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, this.config.moneyFormat);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.config.currency || 'USD'
    }).format(Number(cents || 0) / 100);
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;');
  }
}

if (!customElements.get('cart-drawer-free-shipping')) {
  customElements.define('cart-drawer-free-shipping', CartDrawerFreeShipping);
}
